import React, { useMemo, useState } from 'react';
import {
  IonAvatar,
  IonBadge,
  IonContent,
  IonGrid,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonCol,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import { cashOutline, cartOutline, trendingUpOutline } from 'ionicons/icons';
import { AnalyticsData, OrdersSummaryMetrics, OrderWithDetails } from 'shared';
import MetricCard from '../components/MetricCard';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { formatCurrency, formatDateTime } from '../utils/format';
import { RefresherEvent } from '../types/ionic';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import Notifications from '../components/Notifications';

type Period = 'week' | 'month' | 'year';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [period, setPeriod] = useState<Period>('week');

  const todayQuery = useQuery({
    queryKey: ['analytics', 'today'],
    queryFn: async () => {
      const { data } = await apiClient.get<AnalyticsData>('/analytics/today');
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const periodQuery = useQuery({
    queryKey: ['analytics', 'period', period],
    queryFn: async () => {
      const { data } = await apiClient.get<AnalyticsData>('/analytics/period', {
        params: { period },
      });
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const ordersSummaryQuery = useQuery({
    queryKey: ['orders', 'summary', 'today'],
    queryFn: async () => {
      const { data } = await apiClient.get<OrdersSummaryMetrics>(
        '/analytics/orders/summary',
        { params: { tab: 'today' } },
      );
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const recentOrdersQuery = useQuery({
    queryKey: ['orders', 'recent', 6],
    queryFn: async () => {
      const { data } = await apiClient.get<OrderWithDetails[]>(
        '/orders/recent',
        { params: { limit: 6 } },
      );
      return data;
    },
  });

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await Promise.all([
        todayQuery.refetch(),
        periodQuery.refetch(),
        ordersSummaryQuery.refetch(),
        recentOrdersQuery.refetch(),
      ]);
    } finally {
      event.detail.complete();
    }
  };

  const todayMetrics = useMemo(() => {
    const sales = todayQuery.data?.totalSales ?? 0;
    const orders = todayQuery.data?.ordersCount ?? 0;
    const profit = todayQuery.data?.profit ?? 0;
    const margin = sales ? Math.round((profit / sales) * 100) : 0;

    return [
      {
        id: 'revenue',
        title: t('todaysRevenue'),
        value: formatCurrency(sales),
        context: `${ordersSummaryQuery.data?.todayOrders ?? 0} ${t('orders').toLowerCase()}`,
        icon: cashOutline,
        accent: 'primary' as const,
      },
      {
        id: 'orders',
        title: t('ordersProcessed'),
        value: `${orders}`,
        context: `${ordersSummaryQuery.data?.unpaidOrders ?? 0} ${t('unpaid').toLowerCase()}`,
        icon: cartOutline,
        accent: 'success' as const,
      },
      {
        id: 'profit',
        title: t('netProfit'),
        value: formatCurrency(profit),
        context: margin
          ? `${Math.abs(margin)}% ${t('profit').toLowerCase()}`
          : t('noSalesToday'),
        icon: trendingUpOutline,
        accent: 'warning' as const,
      },
    ];
  }, [
    ordersSummaryQuery.data?.todayOrders,
    ordersSummaryQuery.data?.unpaidOrders,
    todayQuery.data?.ordersCount,
    todayQuery.data?.profit,
    todayQuery.data?.totalCost,
    todayQuery.data?.totalSales,
  ]);

  const topItems = todayQuery.data?.topSellingItems ?? [];
  const periodAnalytics = periodQuery.data;

  const isBusy =
    todayQuery.isLoading &&
    !todayQuery.data &&
    ordersSummaryQuery.isLoading;

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('dashboard')}</IonTitle>
          <Notifications />
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <IonText color="medium">
            <p style={{ marginBottom: 12 }}>
              {user ? `${t('account')}: ${user.username}` : 'Cafe CN'}
            </p>
          </IonText>

          {isBusy ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner name="crescent" />
            </div>
          ) : (
            <>
              <IonGrid>
                <IonRow>
                  {todayMetrics.map((metric) => (
                    <IonCol size="12" sizeMd="6" key={metric.id}>
                      <MetricCard
                        title={metric.title}
                        value={metric.value}
                        context={metric.context}
                        icon={metric.icon}
                        accent={metric.accent}
                      />
                    </IonCol>
                  ))}
                </IonRow>
              </IonGrid>

              <section style={{ marginTop: 24 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <IonText color="primary">
                    <h2 style={{ margin: 0 }}>{t('performanceOverview')}</h2>
                  </IonText>
                  <IonSegment
                    value={period}
                    onIonChange={(event) =>
                      setPeriod((event.detail.value as Period) || 'week')
                    }
                    style={{ maxWidth: 280 }}
                  >
                    <IonSegmentButton value="week">
                      <IonLabel>7D</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="month">
                      <IonLabel>30D</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="year">
                      <IonLabel>YTD</IonLabel>
                    </IonSegmentButton>
                  </IonSegment>
                </div>

                <IonList lines="none">
                  <IonItem>
                    <IonLabel>
                      <h3>{t('totalRevenue')}</h3>
                      <p>{t('revenueTrendTitle')}</p>
                    </IonLabel>
                    <IonText slot="end" color="primary">
                      <strong>
                        {formatCurrency(periodAnalytics?.totalSales ?? 0)}
                      </strong>
                    </IonText>
                  </IonItem>
                  <IonItem>
                    <IonLabel>
                      <h3>{t('profit')}</h3>
                      <p>{t('ordersProcessed')}</p>
                    </IonLabel>
                    <IonText slot="end" color="success">
                      <strong>
                        {formatCurrency(periodAnalytics?.profit ?? 0)}
                      </strong>
                    </IonText>
                  </IonItem>
                  <IonItem>
                    <IonLabel>
                      <h3>{t('orders')}</h3>
                      <p>{t('orderSummary')}</p>
                    </IonLabel>
                    <IonBadge slot="end" color="tertiary">
                      {periodAnalytics?.ordersCount ?? 0}
                    </IonBadge>
                  </IonItem>
                </IonList>
              </section>

              <section style={{ marginTop: 32 }}>
                  <IonText color="primary">
                    <h2 style={{ marginBottom: 12 }}>{t('topSellingItems')}</h2>
                  </IonText>
                {topItems.length === 0 ? (
                  <IonText color="medium">
                    <p>{t('noSalesToday')}</p>
                  </IonText>
                ) : (
                  <IonList>
                    {topItems.map((item, index) => (
                      <IonItem key={item.menuItem.id}>
                        <IonAvatar slot="start">
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              background:
                                'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(129,140,248,0.35))',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                            }}
                          >
                            #{index + 1}
                          </div>
                        </IonAvatar>
                        <IonLabel>
                          <h3>{item.menuItem.product?.name || item.menuItem.id}</h3>
                          <p>
                            {item.quantitySold} {t('itemsSold').toLowerCase()} •{' '}
                            {formatCurrency(item.revenue)}
                          </p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </section>

              <section style={{ marginTop: 32, marginBottom: 32 }}>
                <IonText color="primary">
                  <h2 style={{ marginBottom: 12 }}>{t('recentOrders')}</h2>
                </IonText>
                {recentOrdersQuery.isLoading && !recentOrdersQuery.data ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                    <IonSpinner />
                  </div>
                ) : recentOrdersQuery.data && recentOrdersQuery.data.length > 0 ? (
                  <IonList>
                    {recentOrdersQuery.data.map((order) => (
                      <IonItem key={order.id}>
                        <IonLabel>
                          <h3>{order.customer?.name || 'Walk-in customer'}</h3>
                          <p>{formatDateTime(order.orderDate)}</p>
                          <p>
                            {order.items?.length ?? 0} items •{' '}
                            {formatCurrency(Number(order.totalAmount))}
                          </p>
                        </IonLabel>
                        <IonBadge color={order.isPaid ? 'success' : 'warning'} slot="end">
                          {order.isPaid ? 'Paid' : 'Unpaid'}
                        </IonBadge>
                      </IonItem>
                    ))}
                  </IonList>
                ) : (
                  <IonText color="medium">
                    <p>{t('noOrdersFound')}</p>
                  </IonText>
                )}
              </section>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Dashboard;
